// Bê từ finiva-admin src/modules/auth/components/LoginPage.tsx. Sửa: không
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@shared/utils'
import { BrandLockup } from '@shared/components/ui/brand'
import { useLogin } from '../hooks/useAuth'
import { ROUTES } from '@config/routes'

const schema = z.object({
  email: z.string().email('Email không hợp lệ'),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu'),
})

type FormValues = z.infer<typeof schema>

export function LoginPage() {
  const { t } = useTranslation()
  const { login } = useLogin()
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const onSubmit = async (values: FormValues) => {
    setError(null)
    try {
      await login({ ...values, type: 'admin' })
    } catch {
      setError(t('auth.loginError'))
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-secondary px-4">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-lg shadow-brand-900/5">
        <BrandLockup size="md" tone="onLight" subtitle="Quản trị tài chính" className="mb-7" />

        <div className="mb-6">
          <h1>{t('auth.loginTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('auth.loginSubtitle')}</p>
        </div>

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="email">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="m@example.com"
              className={cn(
                'h-8 w-full rounded-lg border border-input bg-background px-2 text-sm text-foreground',
                'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                errors.email && 'border-destructive focus:border-destructive focus:ring-destructive/20',
              )}
              {...register('email')}
            />
            {errors.email && <p className="text-xs text-state-critical">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground" htmlFor="password">
                {t('auth.password')}
              </label>
              <Link
                to={ROUTES.FORGOT_PASSWORD}
                className="text-xs text-muted-foreground transition-colors hover:text-brand-600"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={cn(
                  'h-8 w-full rounded-lg border border-input bg-background px-2 pr-8 text-sm text-foreground',
                  'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                  errors.password && 'border-destructive focus:border-destructive focus:ring-destructive/20',
                )}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-state-critical">{errors.password.message}</p>}
          </div>

          {error && (
            <p className="rounded-lg bg-state-critical-bg px-3 py-2 text-center text-sm text-state-critical">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-1 h-8 w-full rounded-lg bg-brand-500 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>
      </div>
    </div>
  )
}
